"use client";

import { useEffect } from "react";

/**
 * The error boundary for every route under the root layout.
 *
 * The panel had none. That is not a theoretical gap: on the day an RSC
 * function-prop serialisation error took the home page down, `tsc --noEmit`
 * passed, `next build` passed, `/api/health` said ok, and the screen Fred lands
 * on after signing in returned a bare 500 with nothing to grep for. The fix at
 * the time was a static test (src/components/boundary.test.ts) that catches
 * that ONE class before it ships. This catches every other class after it does.
 *
 * Three deliberate choices:
 *
 *  - The digest is SHOWN, not hidden. Next replaces the real message with an
 *    opaque digest in production and logs the original server-side; the digest
 *    is the only handle tying this screen to that log line. Hiding it is a
 *    courtesy to strangers, and this panel has one user.
 *  - It says the rest of the panel still works, because it does. A route error
 *    boundary is scoped: the nav, the other screens and the API are unaffected,
 *    and an operator who does not know that treats one broken page as an
 *    outage.
 *  - It logs through console.error, which under PM2 is already a timestamped
 *    line in ~/.pm2/logs/panel-web-error.log (ecosystem.config.js sets
 *    `time: true`). No logging dependency buys anything the box cannot do.
 *
 * This renders inside src/app/layout.tsx but OUTSIDE (panel)/layout.tsx, so a
 * failing panel screen loses the nav. That is the correct trade: the nav is
 * itself a plausible culprit, and a boundary that re-renders its own suspect is
 * a boundary that throws twice.
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("[panel] render error", {
      digest: error.digest ?? null,
      message: error.message,
      path: typeof window === "undefined" ? "?" : window.location.pathname,
    });
  }, [error]);

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-6">
      <div className="space-y-3 rounded-lg border border-bad/40 bg-panel2/50 p-5">
        <h1 className="text-lg font-bold text-bad">This screen failed to render</h1>
        <p className="text-sm text-dim">
          The rest of the panel is unaffected — the nav, the other screens and the API are still
          working. To find this in the log, grep the digest below in{" "}
          <code className="font-mono text-xs">pm2 logs panel-web</code>.
        </p>

        <pre className="logpane overflow-x-auto rounded-lg bg-panel2 p-3 font-mono text-xs text-dim">
          digest: {error.digest ?? "(none — this error was thrown on the client)"}
          {"\n"}
          {error.message}
        </pre>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={reset}
            className="rounded-lg border border-line px-3 py-1.5 text-xs font-medium text-dim transition hover:bg-panel2"
          >
            try again
          </button>
          <a
            href="/"
            className="rounded-lg border border-line px-3 py-1.5 text-xs font-medium text-dim transition hover:bg-panel2"
          >
            back to the dashboard
          </a>
        </div>
      </div>
    </div>
  );
}
